/**
 * HomepageV3 demo content — EXACT copy of what the editorial design renders.
 *
 * Ported verbatim from:
 *  - design-refs/source/private-home.js  (CO, FILTER_META, SIDE_GROUPS, SECTIONS, hero/stats, spotlights)
 *  - design-refs/source/tss-home.js      (CARD_* alt-homepage card data)
 *
 * This lets section agents render the design with zero auth / no live data.
 * Accuracy matters — names, series, sectors, invested figures, reg lines,
 * badges, fill %, and minimums are reproduced 1:1 from the source.
 */

/* ─────────────── Company master data (CO) ─────────────── */

export interface Company {
  /** Key in the COMPANIES map */
  key: string;
  /** image-slot id from source (logo placement) */
  slot?: string;
  /** Logo asset path (relative to design-refs source) */
  logo?: string;
  /** Offering tombstone/cover image used by image-first experiment variants. */
  tombstoneImage?: string;
  /** Optional white wordmark for image-first experiment tiles. */
  bannerLogo?: string;
  /** Single-letter monogram used when no logo asset */
  mono?: string;
  /** object-fit hint for banner/photo logos */
  fit?: 'cover' | 'contain';
  /** Optional brand background used by the experimental image-first tombstones. */
  tileBg?: string;
  /** Renders as a banner-photo card */
  banner?: boolean;
  /** Known valid offering route for live data-backed cards. */
  href?: string;
  name: string;
  series: string;
  /** Legal entity / series vehicle from onboarding (offering `company_name`),
   * e.g. "Series 89-1, a series of StartEngine Private LLC" — named in the Reg D
   * membership-interest disclosure. */
  companyName?: string;
  sector: string;
  invested: string;
  desc: string;
  reg: string;
  regcf?: boolean;
  regA?: boolean;
  regD?: boolean;
}

export const COMPANIES: Record<string, Company> = {
  openai: {
    key: 'openai',
    slot: 'logo-openai',
    logo: 'assets/logos/openai.svg',
    bannerLogo: '/own/landing-page/assets/logos/openai-wordmark-white.png',
    tileBg: '#10A37F',
    name: 'OpenAI',
    series: 'Series OpenAI-5',
    sector: 'AI · Foundation Models',
    invested: '$5.1M',
    desc: "Creator of ChatGPT — the world's most used AI product with $2B+ annualized revenue.",
    reg: 'Reg D via StartEngine Primary, LLC',
  },
  polymarket: {
    key: 'polymarket',
    slot: 'logo-polymarket',
    logo: 'assets/logos/polymarket.svg',
    bannerLogo: '/homepage-v3/logos/white/polymarket.png',
    tileBg: '#2A5CF5',
    name: 'Polymarket',
    series: 'Series Polymarket-2',
    sector: 'Prediction Markets',
    invested: '$3.2M',
    desc: 'One of the largest prediction marketplaces with over $1B in monthly volume.',
    reg: 'Reg D via StartEngine Primary, LLC',
  },
  disruptor: {
    key: 'disruptor',
    slot: 'logo-disruptors',
    logo: 'assets/logos/disruptors.svg',
    tileBg: '#0B292D',
    name: 'The Disruptors Fund 2',
    series: 'The Disruptors Fund 2',
    sector: '',
    invested: '$3.94M',
    desc: 'Exposure to OpenAI, Polymarket & more with a single investment.',
    reg: 'Reg D via StartEngine Primary, LLC',
  },
  disruptorqp: {
    key: 'disruptorqp',
    slot: 'logo-disruptors',
    logo: 'assets/logos/disruptors.svg',
    tileBg: '#0B292D',
    name: 'The Disruptors Fund 2 QP',
    series: 'The Disruptors Fund 2 QP',
    sector: '',
    invested: '$1.45M',
    desc: 'QP-exclusive exposure to OpenAI, Polymarket & more with enhanced terms.',
    reg: 'Reg D via StartEngine Primary, LLC',
  },
  psi: {
    key: 'psi',
    slot: 'logo-psiquantum',
    logo: 'assets/logos/psiquantum.svg',
    tileBg: '#241A54',
    name: 'PsiQuantum',
    series: 'Series PsiQuantum-IRA-1',
    sector: 'Quantum Computing',
    invested: '$1.8M',
    desc: "IRA-exclusive access to the world's first fault-tolerant quantum computer.",
    reg: 'Reg D via StartEngine Primary, LLC',
  },
  atombeam: {
    key: 'atombeam',
    slot: 'logo-atombeam',
    logo: 'assets/logos/atombeam-photo.png',
    fit: 'cover',
    banner: true,
    name: 'Atombeam',
    series: 'Reg CF',
    sector: 'Data Compression · AI',
    invested: '$1.1M',
    desc: 'Patented tech shrinking data by 75% for IoT, satellites & edge computing.',
    reg: 'Reg CF via StartEngine Primary, LLC',
    regcf: true,
  },
  liquidpiston: {
    key: 'liquidpiston',
    slot: 'logo-liquidpiston',
    logo: 'assets/logos/liquidpiston.png',
    fit: 'cover',
    banner: true,
    name: 'LiquidPiston',
    series: 'Reg A+',
    sector: 'Compact Engines · Defense',
    invested: '$2.4M',
    desc: 'Reinventing the internal combustion engine — compact, multi-fuel rotary power for defense and portable generation.',
    reg: 'Reg A+ via StartEngine Primary, LLC',
    regA: true,
  },
  etherdyne: {
    key: 'etherdyne',
    mono: 'E',
    name: 'Etherdyne Technologies',
    series: 'Common Stock · Reg CF',
    sector: 'Wireless Power · Edge Tech',
    invested: '$1.2M',
    desc: 'Through-the-air wireless power with 44 granted patents and FCC + CE clearance — now licensing its Ether Power platform to device makers.',
    reg: 'Reg CF via StartEngine Capital LLC',
    regcf: true,
  },
  boxabl: {
    key: 'boxabl',
    mono: 'B',
    name: 'BoxAbl',
    series: 'Common Stock · Reg A+',
    sector: 'Modular Housing',
    invested: '$12.6M',
    desc: 'Factory-built foldable homes that ship flat and unfold on a foundation in hours — among the most-funded campaigns in crowdfunding history.',
    reg: 'Reg A+ via StartEngine Primary, LLC',
    regA: true,
  },
  modemobile: {
    key: 'modemobile',
    mono: 'M',
    name: 'Mode Mobile',
    series: 'Common Stock · Reg A+',
    sector: 'Consumer · Mobile',
    invested: '$5.8M',
    desc: 'The EarnPhone and EarnOS turn everyday smartphone activity into income — a Deloitte Technology Fast 500 company.',
    reg: 'Reg A+ via StartEngine Primary, LLC',
    regA: true,
  },
  flyingeyes: {
    key: 'flyingeyes',
    mono: 'F',
    name: 'Flying Eyes Optics',
    series: 'Reg CF',
    sector: 'Performance Eyewear',
    invested: '$640K',
    desc: 'Ultra-light, unbreakable optical eyewear built for pilots and athletes — a StartEngine top-momentum raise.',
    reg: 'Reg CF via StartEngine Capital LLC',
    regcf: true,
  },
  gacw: {
    key: 'gacw',
    mono: 'G',
    name: 'Global Air Cylinder Wheels',
    series: 'Common Stock · Reg CF',
    sector: 'Heavy Industry · Mobility',
    invested: '$2.1M',
    desc: 'Airless cylinder wheels that replace pneumatic tires on mining trucks and heavy equipment — now on its third crowdfunding campaign.',
    reg: 'Reg CF via StartEngine Capital LLC',
    regcf: true,
  },
  nineline: {
    key: 'nineline',
    mono: 'N',
    name: 'Nine Line Apparel',
    series: 'Common Stock · Reg CF',
    sector: 'Apparel · American-Made',
    invested: '$1.4M',
    desc: 'Veteran-founded patriotic apparel brand manufacturing in the USA, with a loyal nationwide customer base.',
    reg: 'Reg CF via StartEngine Capital LLC',
    regcf: true,
  },
  gigawatt: {
    key: 'gigawatt',
    mono: 'G',
    name: 'Gigawatt',
    series: 'Common Stock · Reg CF',
    sector: 'Clean Energy · Solar',
    invested: '$980K',
    desc: 'Reviving the Real Goods solar brand for residential and community solar across the United States.',
    reg: 'Reg CF via StartEngine Capital LLC',
    regcf: true,
  },
  kingscrowd: {
    key: 'kingscrowd',
    mono: 'K',
    name: 'Kingscrowd',
    series: 'Reg CF',
    sector: 'Fintech · Private-Market Data',
    invested: '$420K',
    desc: 'Private-market investment analytics — the data and ratings platform billed as the Bloomberg of private offerings.',
    reg: 'Reg CF via StartEngine Capital LLC',
    regcf: true,
    logo: 'https://d19j0qt0x55bap.cloudfront.net/production/startups/6827a85dd706e96dc6d31446/images/startup_cover/tombstone_image1.jpg',
    tombstoneImage:
      'https://d19j0qt0x55bap.cloudfront.net/production/startups/6827a85dd706e96dc6d31446/images/startup_cover/tombstone_image1.jpg',
    banner: true,
    fit: 'cover',
    href: '/offering/kingscrowd',
  },
};

/* ─────────────── Filter / availability metadata (FILTER_META) ─────────────── */

export type BadgeClass = 'bdg-teal' | 'bdg-gold' | 'bdg-wine';

export interface Badge {
  cls: BadgeClass;
  label: string;
  dot?: boolean;
}

export interface FilterMeta {
  /** Industry group */
  ind: string;
  /** Minimum-investment tier */
  min: string;
  /** Accreditation requirement */
  acc: string;
  badge: Badge;
  /** Availability copy (e.g. "12% left" / "79% funded" / "Open") */
  avail: string;
  /** Urgency class: "urg" (urgent) or "ok" */
  cls: 'urg' | 'ok';
  /** Progress fill width (0–100) */
  fillW: number;
}

export const FILTER_META: Record<string, FilterMeta> = {
  openai: {
    ind: 'AI & Infrastructure',
    min: '$2.5K+',
    acc: 'Accredited only',
    badge: { cls: 'bdg-teal', label: 'Trending' },
    avail: '12% left',
    cls: 'urg',
    fillW: 88,
  },
  polymarket: {
    ind: 'Prediction Markets',
    min: '$2.5K+',
    acc: 'Accredited only',
    badge: { cls: 'bdg-gold', label: 'Waitlist' },
    avail: '44% left',
    cls: 'ok',
    fillW: 56,
  },
  disruptor: {
    ind: 'Multi-company funds',
    min: 'From $500',
    acc: 'IRA-eligible',
    badge: { cls: 'bdg-gold', label: 'From $500' },
    avail: 'Open',
    cls: 'ok',
    fillW: 55,
  },
  disruptorqp: {
    ind: 'Multi-company funds',
    min: '$2.5K+',
    acc: 'Qualified purchasers',
    badge: { cls: 'bdg-wine', dot: true, label: '24% left' },
    avail: '24% left',
    cls: 'urg',
    fillW: 76,
  },
  psi: {
    ind: 'Quantum',
    min: '$1K – $2.5K',
    acc: 'IRA-exclusive',
    badge: { cls: 'bdg-gold', label: 'IRA-exclusive' },
    avail: '61% left',
    cls: 'ok',
    fillW: 39,
  },
  atombeam: {
    ind: 'AI & Infrastructure',
    min: 'From $500',
    acc: 'Open to all',
    badge: { cls: 'bdg-gold', label: 'From $1,000' },
    avail: '68% left',
    cls: 'ok',
    fillW: 32,
  },
  liquidpiston: {
    ind: 'Defense',
    min: 'From $500',
    acc: 'Open to all',
    badge: { cls: 'bdg-gold', label: 'From $500' },
    avail: '73% left',
    cls: 'ok',
    fillW: 27,
  },
  etherdyne: {
    ind: 'Wireless Power',
    min: 'From $500',
    acc: 'Open to all',
    badge: { cls: 'bdg-teal', label: 'Trending' },
    avail: '79% funded',
    cls: 'ok',
    fillW: 79,
  },
  boxabl: {
    ind: 'Modular Housing',
    min: '$1K – $2.5K',
    acc: 'Open to all',
    badge: { cls: 'bdg-gold', label: 'Popular' },
    avail: '71% funded',
    cls: 'ok',
    fillW: 71,
  },
  modemobile: {
    ind: 'Consumer',
    min: 'From $500',
    acc: 'Open to all',
    badge: { cls: 'bdg-teal', label: 'Trending' },
    avail: '66% funded',
    cls: 'ok',
    fillW: 66,
  },
  flyingeyes: {
    ind: 'Consumer',
    min: 'From $500',
    acc: 'Open to all',
    badge: { cls: 'bdg-gold', label: 'From $200' },
    avail: '58% funded',
    cls: 'ok',
    fillW: 58,
  },
  gacw: {
    ind: 'Heavy Industry',
    min: 'From $500',
    acc: 'Open to all',
    badge: { cls: 'bdg-wine', dot: true, label: '6 days left' },
    avail: '88% funded',
    cls: 'urg',
    fillW: 88,
  },
  nineline: {
    ind: 'Consumer',
    min: 'From $500',
    acc: 'Open to all',
    badge: { cls: 'bdg-wine', dot: true, label: 'Closing soon' },
    avail: '84% funded',
    cls: 'urg',
    fillW: 84,
  },
  gigawatt: {
    ind: 'Clean Energy',
    min: 'From $500',
    acc: 'Open to all',
    badge: { cls: 'bdg-wine', dot: true, label: 'Closing soon' },
    avail: '82% funded',
    cls: 'urg',
    fillW: 82,
  },
  kingscrowd: {
    ind: 'Fintech',
    min: 'From $500',
    acc: 'Open to all',
    badge: { cls: 'bdg-gold', label: 'From $250' },
    avail: '41% funded',
    cls: 'ok',
    fillW: 41,
  },
};

/* ─────────────── Filter sidebar groups (SIDE_GROUPS) ─────────────── */

export type SideItemType = 'browse' | 'ind' | 'liveInd' | 'min' | 'acc';

export interface SideItem {
  label: string;
  type: SideItemType;
  key: string;
}

export interface SideGroup {
  label: string;
  items: SideItem[];
}

export const SIDE_GROUPS: SideGroup[] = [
  {
    label: 'Browse by',
    items: [
      { label: 'Trending now', type: 'browse', key: 'Trending now' },
      { label: 'Closing soon', type: 'browse', key: 'Closing soon' },
      { label: 'Low minimums', type: 'browse', key: 'Low minimum offerings' },
    ],
  },
  {
    label: 'Industries',
    items: [
      'AI & Infrastructure',
      'Prediction Markets',
      'Quantum',
      'Defense',
      'Aerospace',
      'Fintech',
      'Multi-company funds',
    ].map((l) => ({ label: l, type: 'ind' as const, key: l })),
  },
  {
    // Paige (2026-07-07): two buckets keyed by minimum investment. Crowdfunding
    // (Reg CF/A+) lands in "Under $5K" and private (Reg D) in "$5K+", with the
    // real per-offering minimum overriding that default (see FilterResults).
    label: 'Minimum',
    items: [
      { label: 'Under $5K', type: 'min' as const, key: 'under-5k' },
      { label: '$5K+', type: 'min' as const, key: '5k-plus' },
    ],
  },
];

/* ─────────────── Homepage hero + stat rail ─────────────── */

export const HERO = {
  eyebrow: 'StartEngine Private',
  /** `<em>` segment renders italic / champagne — the "pre-IPO companies." accent */
  title: "Access the world's most sought-after",
  titleEm: 'pre-IPO companies.',
} as const;

export interface HeroStat {
  val: string;
  label: string;
}

export const HERO_STATS: HeroStat[] = [
  { val: '$1.5B', label: 'Invested on the platform' },
  { val: '2.1M', label: 'Community' },
  { val: '1,000', label: 'Opportunities' },
  { val: '13 Yrs', label: 'In business' },
];

/* ─────────────── Featured spotlights ─────────────── */

export interface Spotlight {
  eyebrowLabel: string;
  series: string;
  name: string;
  desc: string;
  /** plain-text fragments wrapped in <strong> in the design */
  emphasis?: string[];
  cta: string;
  image?: string;
  /** Reg-aware legal disclosure for the featured offering (live-built). */
  disclosureLegal?: string;
}

/** Default homepage featured offering (Disruptors Fund 2). */
export const SPOTLIGHT_DISRUPTORS: Spotlight = {
  eyebrowLabel: 'Featured Offering',
  series: 'Series 4-1 · Reg D',
  name: 'The Disruptors Fund 2',
  desc: "One investment for diversified exposure to today's most sought-after private AI companies — including OpenAI, Anthropic, and Polymarket.",
  emphasis: ['OpenAI, Anthropic, and Polymarket'],
  cta: 'View details',
  image: '/homepage-v3/disruptors-fund-2.png',
};

/** OWN8 featured variant (StartEngine itself). */
export const SPOTLIGHT_OWN8: Spotlight = {
  eyebrowLabel: 'Featured Offering',
  series: 'Reg A+',
  name: 'StartEngine',
  desc: 'Invest in the platform powering private markets. Revenue has nearly doubled two years running — $23M → $48M → $109M — and the company is now profitable, with $1.5B+ deployed across 2M+ users. Now you can own a piece of StartEngine itself.',
  emphasis: ['Now you can own a piece of StartEngine itself.'],
  cta: 'View details',
};

/* ─────────────── Homepage offering sections (SECTIONS) ─────────────── */

export interface CardFigure {
  lbl: string;
  val: string;
  cls?: 'urg' | 'ok' | 'amber';
}

export interface CardFill {
  w: number;
  cls?: 'urg' | 'ok' | 'amber';
}

export interface OfferingCardData {
  /** Company key into COMPANIES */
  co: string;
  /** Featured / larger card */
  ft?: boolean;
  badge?: Badge;
  meta?: string;
  /** Per-card description override (otherwise use COMPANIES[co].desc) */
  desc?: string;
  /** Left figure (e.g. Minimum) */
  figL?: CardFigure;
  /** Right figure (e.g. Availability / Funded / Invested) */
  figR?: CardFigure;
  /** Crowdfunding investor count, shown only when campaign metrics are enabled. */
  numberOfInvestors?: number;
  fill?: CardFill;
  /** Extra normalized tokens used by Explore search. */
  searchTerms?: string[];
  /** Industry/category display names from the offering's tombstone `categories`. */
  categories?: string[];
  /** Non-offering cards can override the auto regulatory disclosure. */
  disclosure?: string;
  /** Non-offering cards can suppress the progress track. */
  hideProgress?: boolean;
}

export interface OfferingSection {
  eyebrow: string;
  title: string;
  desc: string;
  cards: OfferingCardData[];
  /** Ordering-disclosure tooltip shown next to the shelf title. */
  tip?: string;
}

export const SECTIONS: OfferingSection[] = [
  {
    eyebrow: 'Most momentum',
    title: 'Trending now',
    desc: 'Highest investment activity recently',
    cards: [
      {
        co: 'openai',
        ft: true,
        meta: 'Accredited only',
        figR: { lbl: 'Availability', val: '12% left', cls: 'urg' },
        fill: { w: 88, cls: 'urg' },
      },
      {
        co: 'polymarket',
        ft: true,
        badge: { cls: 'bdg-gold', label: 'Waitlist' },
        meta: 'Previously sold out',
        figR: { lbl: 'Availability', val: '0% left', cls: 'urg' },
        fill: { w: 100, cls: 'urg' },
      },
      {
        co: 'disruptor',
        badge: { cls: 'bdg-wine', label: 'Closing soon' },
        meta: 'IRA Exclusive',
        figR: { lbl: 'Availability', val: 'Open', cls: 'ok' },
        fill: { w: 55, cls: 'ok' },
      },
      {
        co: 'psi',
        meta: 'IRA Exclusive',
        figR: { lbl: 'Availability', val: '61% left', cls: 'ok' },
        fill: { w: 39, cls: 'ok' },
      },
      {
        co: 'etherdyne',
        ft: true,
        meta: 'Popular this week',
        figR: { lbl: 'Funded', val: '79%', cls: 'ok' },
        fill: { w: 79, cls: 'ok' },
      },
      {
        co: 'boxabl',
        badge: { cls: 'bdg-gold', label: 'Popular' },
        meta: '38,400 investors',
        figR: { lbl: 'Funded', val: '71%', cls: 'ok' },
        fill: { w: 71, cls: 'ok' },
      },
      {
        co: 'modemobile',
        meta: 'Deloitte Fast 500',
        figR: { lbl: 'Funded', val: '66%', cls: 'ok' },
        fill: { w: 66, cls: 'ok' },
      },
    ],
  },
  {
    eyebrow: 'Private markets',
    title: 'Pre-IPO Deals',
    desc: 'Private-market offerings for accredited investors',
    cards: [
      {
        co: 'openai',
        ft: true,
        meta: 'Accredited only',
        figR: { lbl: 'Availability', val: '12% left', cls: 'urg' },
        fill: { w: 88, cls: 'urg' },
      },
      {
        co: 'polymarket',
        ft: true,
        badge: { cls: 'bdg-gold', label: 'Waitlist' },
        figR: { lbl: 'Availability', val: '0% left', cls: 'urg' },
        fill: { w: 100, cls: 'urg' },
      },
      {
        co: 'disruptorqp',
        meta: 'Qualified Purchasers Exclusive',
        figR: { lbl: 'Availability', val: 'Open', cls: 'ok' },
        fill: { w: 45, cls: 'ok' },
      },
      {
        co: 'psi',
        meta: 'IRA Exclusive',
        figR: { lbl: 'Availability', val: '61% left', cls: 'ok' },
        fill: { w: 39, cls: 'ok' },
      },
    ],
  },
  {
    eyebrow: 'Limited availability',
    title: 'Closing soon',
    desc: 'Due to least inventory or closing offering soon',
    cards: [
      {
        co: 'openai',
        ft: true,
        figR: { lbl: 'Remaining', val: '12%', cls: 'urg' },
        fill: { w: 88, cls: 'urg' },
      },
      {
        co: 'polymarket',
        ft: true,
        desc: 'One of the largest prediction marketplaces with over $1B monthly volume.',
        figR: { lbl: 'Remaining', val: '19%', cls: 'urg' },
        fill: { w: 81, cls: 'urg' },
      },
      {
        co: 'disruptorqp',
        figR: { lbl: 'Remaining', val: '24%', cls: 'urg' },
        fill: { w: 76, cls: 'urg' },
      },
      { co: 'gacw', figR: { lbl: 'Funded', val: '88%', cls: 'urg' }, fill: { w: 88, cls: 'urg' } },
      {
        co: 'nineline',
        figR: { lbl: 'Funded', val: '84%', cls: 'urg' },
        fill: { w: 84, cls: 'urg' },
      },
      {
        co: 'gigawatt',
        figR: { lbl: 'Funded', val: '82%', cls: 'urg' },
        fill: { w: 82, cls: 'urg' },
      },
    ],
  },
  {
    eyebrow: 'Accessible investing',
    title: 'Low minimum offerings',
    desc: 'Crowdfunding offerings ranked by recent activity',
    cards: [
      {
        co: 'liquidpiston',
        ft: true,
        figL: { lbl: 'Minimum', val: '$500', cls: 'amber' },
        figR: { lbl: 'Invested', val: '$2.4M' },
        fill: { w: 73, cls: 'ok' },
      },
      {
        co: 'atombeam',
        ft: true,
        figL: { lbl: 'Minimum', val: '$1,000', cls: 'amber' },
        figR: { lbl: 'Invested', val: '$1.1M' },
        fill: { w: 68, cls: 'amber' },
      },
      {
        co: 'flyingeyes',
        figL: { lbl: 'Minimum', val: '$200', cls: 'amber' },
        figR: { lbl: 'Invested', val: '$640K' },
        fill: { w: 58, cls: 'ok' },
      },
      {
        co: 'kingscrowd',
        figL: { lbl: 'Minimum', val: '$250', cls: 'amber' },
        figR: { lbl: 'Invested', val: '$420K' },
        fill: { w: 41, cls: 'ok' },
      },
    ],
  },
];

/* ─────────────── Alt-homepage card data (tss-home.js) ─────────────── */

export interface AltCard {
  /** logo key into COMPANIES */
  logo: string;
  /** Tag labels (e.g. "AI & Infra", "Trending Offer", "Closing Soon") */
  tags: string[];
  series: string;
  desc: string;
  invested: string;
  /** Availability copy */
  avail: string;
  /** Urgency flag */
  availUrg: boolean;
  /** Progress fill (0–100) */
  fill: number;
  min: string;
  reg: string;
}

const CARD_OPENAI: AltCard = {
  logo: 'openai',
  tags: ['AI & Infra', 'Trending Offer'],
  series: 'Series OpenAI-5',
  desc: "Creator of ChatGPT — the world's most used AI product with $2B+ annualized revenue.",
  invested: '$866K',
  avail: '12% left',
  availUrg: true,
  fill: 88,
  min: '$25K',
  reg: 'Membership interest in Series 73-2 ("Series Polymarket-2") will own Series B Preferred Stock of Blockratize Inc. Not buying shares directly.',
};

const CARD_POLY: AltCard = {
  logo: 'polymarket',
  tags: ['Computing'],
  series: 'Series Polymarket-2',
  desc: 'One of the largest prediction marketplaces with over $1B in monthly volume.',
  invested: '$3.2M',
  avail: '55% left',
  availUrg: false,
  fill: 45,
  min: '$2.5K',
  reg: 'Membership interest in Series 73-2 ("Series Polymarket-2") will own Series B Preferred Stock of Blockratize Inc. Not buying shares directly.',
};

const CARD_ATOM: AltCard = {
  logo: 'atombeam',
  tags: ['Data', 'Closing Soon'],
  series: 'Series PsiQuantum-IRA-1',
  desc: 'Patented tech shrinking data by 75% for IoT, satellites & edge computing.',
  invested: '$1.8M',
  avail: '12% left',
  availUrg: true,
  fill: 88,
  min: '$2,500',
  reg: 'Membership interest in Series 73-2 ("Series Polymarket-2") will own Series B Preferred Stock of Blockratize Inc. Not buying shares directly.',
};

const CARD_OPENAI2: AltCard = { ...CARD_OPENAI, tags: ['AI & Infra'], min: '$2,000' };

export const ALT_CARDS = { CARD_OPENAI, CARD_POLY, CARD_ATOM, CARD_OPENAI2 } as const;

export interface AltSection {
  /** Section label */
  label: string;
  cards: AltCard[];
}

export const ALT_SECTIONS: AltSection[] = [
  { label: 'Trending Offer', cards: [CARD_OPENAI, CARD_POLY, CARD_ATOM, CARD_OPENAI2] },
  {
    label: 'Closing Soon',
    cards: [
      { ...CARD_OPENAI, min: '$2,000' },
      { ...CARD_POLY, min: '$2,500' },
      { ...CARD_ATOM, min: '$2,500' },
      CARD_OPENAI2,
    ],
  },
];

/* ─────────────── Announcement banner ─────────────── */

export const BANNER = {
  text: 'StartEngine Private — pre-IPO access for accredited investors',
  ipoChip: 'PRE-IPO',
} as const;

/** Helper: a company is open to all if it is Reg CF or Reg A+. */
export const isOpenToAll = (coKey: string): boolean => {
  const c = COMPANIES[coKey];
  return Boolean(c && (c.regcf || c.regA));
};
